import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrencies } from "@insite/client-framework/Store/Context/ContextSelectors";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showIcon = "showIcon",
}

const mapStateToProps = (state: ApplicationState) => ({
    currencies: getCurrencies(state),
    currentCurrencyId: state.context.session.currency?.id,
    currentCurrencyCode: state.context.session.currency?.currencyCode,
    currentCurrencySymbol: state.context.session.currency?.currencySymbol,
});

const mapDispatchToProps = {
    setCurrency,
};

export interface CurrencyMenuStyles {
    currencyWrapper?: InjectableCss;
    currencySelect?: SelectPresentationProps;
    currencySymbol?: TypographyPresentationProps;
}

export const currencyMenuStyles: CurrencyMenuStyles = {
    currencyWrapper: {
        css: css`
            display: inline-flex;
            align-items: center;
            padding-left: 15px;
            // margin-right: 15px;
            border-left: 1px solid #ccc;
            // border-right: 1px solid #ccc;
            line-height: 1.3;
            position: relative;
            cursor: pointer;

            &:focus:not(:focus-visible) {
                outline: none;
            }
            &:focus-visible {
                outline: 2px dashed #2060ad;
            }

            div[class^="FormInputWrapper"],
            div[class*=" FormInputWrapper"] {
                width: 72px;
                &:focus-visible {
                    background: none;
                    border: none;
                    outline: none !important;

                    select[data-test-selector="currencyMenu"] {
                        background: none;
                        border: none;
                    }
                }
            }

            .dropdown-header {
                background: none;
                border: none;
                color: #535e64;
                font-family: "Roboto",sans-serif;
                font-size: .875rem;
                padding-right: 15px;
                height: auto;
                position: relative;
                font-weight: bold;

                &:focus:not(:focus-visible) {
                    outline: none;
                }
                &:focus-visible {
                    outline: 2px dashed #2060ad;
                }
                
                .arrows {
                    margin-right: 0;
                    margin-left: 5px;
                    width: 14px;
                    height: 14px;
                    display: inline-flex;
                    align-items: center;
                    vertical-align: middle;

                    svg {
                        color: #535e64;
                        font-size: .875rem;
                        width: 14px;
                        height: 14px;
                    }

                }

                .up-arrow {
                    display: none;
                }

                &.active {
                    .up-arrow {
                        display: inline-block;
                        vertical-align: middle;
                        width: 14px;
                        height: 14px;

                        svg {
                            color: #535e64;
                            font-size: .875rem;
                        }
                    }
                    .down-arrow {
                        display: none;
                    }
                }
            }

            .dropdown-list {
                position: absolute;
                right: 0;
                bottom: auto;
                top: 100%;
                z-index: 99;
                padding: 12px;
                background: #fff;
                border: 1px solid #c3d4ea;
                width: 143px;
                max-height: 335px;
                overflow-y: auto;
                box-shadow: rgba(0,37,84,0.15);

                .dropdown-item {
                    cursor: pointer;
                    padding: 17px 23px;
                    border-top: 1px solid #c2d1d9;
                    color: #2e384d;
                    letter-spacing: 0.17px;
                    font-family: 'Lato', sans-serif;

                    &:focus:not(:focus-visible) {
                        outline: none;
                    }
                    &:focus-visible {
                        outline: 2px dashed #2060ad;
                    }

                    &:first-child {
                        border-top: none;
                    }

                    &.current {
                        color: #2e384d;
                        background: #f3f8fe;
                        font-weight: bold;
                        text-decoration: underline;
                    }

                    &:hover {
                        color: #2e384d;
                        background: #f3f8fe;
                        font-weight: bold;
                        text-decoration: underline;
                    }
                }
            }
        `,
    },
    currencySelect: {
        backgroundColor: "transparent",
        cssOverrides: {
            formInputWrapper: css`
                width: 100px;
            `,
            inputSelect: css`
                border: none;
                text-transform: uppercase;
                background: none;
            `,
           
        },
    },
    currencySymbol: {
        size: 14,
        css: css`
            margin-top: 0;
            margin-right: 5px;
            color: #535e64;
            font-size: .875rem;
            font-weight: bold;
            display: inline-block;
            vertical-align: middle;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const CurrencyMenu: FC<Props> = ({
    currencies,
    currentCurrencyId,
    currentCurrencyCode,
    currentCurrencySymbol,
    fields,
    setCurrency,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    if (!currencies || currencies.length <= 1) {
        return null;
    }

    const toggleCurrencyMenu = () => {
        setIsOpen(true);
    };
    
    const handleSelect = (currencyId: string) => {
        setCurrency({ currencyId });
        setIsOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.keyCode === 27) {
            e.stopPropagation();
            setIsOpen(false);
        }
        if (e.keyCode === 13) {
            e.stopPropagation();
            setIsOpen(!isOpen);
        }
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };
    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const styles = useMergeStyles("currencyMenu", currencyMenuStyles);
    const menuId = "currencyMenu";

    if ((typeof window !== "undefined") && (window as any).showCurrencyIcon === undefined) {
        (window as any).showCurrencyIcon = fields.showIcon;
    }

    return (
        <StyledWrapper {...styles.currencyWrapper} onClick={toggleCurrencyMenu} 
        onKeyDown={(e) => handleKeyDown(e)}
        tabIndex={0}
        ref={wrapperRef}>
            {fields.showIcon ? <Typography as="div" {...styles.currencySymbol}>{currentCurrencySymbol}</Typography> : null}
            <VisuallyHidden as="label" id={`${menuId}-label`} htmlFor={menuId}>
                {translate("Currency")}
            </VisuallyHidden>
            <Typography as="div">
                <Typography as="div" className={`dropdown-header ${isOpen ? 'active' : ' '}`}>
                    {currentCurrencyCode}
                    <Icon className="arrows down-arrow" src="ChevronDown" />
                    <Icon className="arrows up-arrow" src="ChevronUp" />
                </Typography>
                {isOpen && (
                    <Typography as="div" className="dropdown-list">
                    {currencies.map((c) => (
                        <Typography
                            as="div"
                            key={c.id}
                            onClick={() => handleSelect(c.id)}
                            className={`dropdown-item ${c.currencyCode === currentCurrencyCode ? 'current' : ' '}`}
                            tabIndex={0}
                        >
                            {c.currencyCode}
                        </Typography>
                    ))}
                    </Typography>
                )}
            </Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CurrencyMenu),
    definition: {
        displayName: "Currency Menu",
        icon: "money-bill",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
